<template>
  <b-card no-body class="info-card">
    <b-card-header class="info-header">
      <div class="header-title">
        <i class="fas fa-info-circle mr-2"></i>
        <strong>Informações</strong>
      </div>
      <div class="header-info">
        O denunciante visualizou o andamento desta denúncia
        {{ complaint.protocolViewCount || 0 }}
        {{ complaint.protocolViewCount === 1 ? "vez" : "vezes" }}.
        <span v-if="complaint.protocolLastView"
          >Última visualização
          {{ complaint.protocolLastView | datetime }}.</span
        >
      </div>
    </b-card-header>
    <b-card-body class="info-body">
      <fieldset class="pb-3">
        <legend hidden>Informações da Denúncia</legend>
        <b-row>
          <b-col cols="6">
            <h5>
              Protocolo: <strong>{{ complaint.protocol }}</strong>
              <small v-if="complaint.previousProtocol">
                (Procotolo anterior: {{ complaint.previousProtocol }})
                <b-button
                  v-if="previousProtocolId"
                  variant="link"
                  size="sm"
                  target="_blank"
                  rel="noreferrer noopener"
                  :to="
                    previousProtocolType === 'ombudsman'
                      ? `/ouvidoria/${previousProtocolId}`
                      : `/denuncias/${previousProtocolId}`
                  "
                >
                  <i class="fas fa-external-link-alt" aria-hidden="true"></i>
                </b-button>
              </small>
            </h5>
          </b-col>
          <b-col cols="6">
            <h5>
              Status:
              <b-badge :variant="getBadgeColor(complaint.status)">{{
                getStatusText(complaint.status)
              }}</b-badge>
            </h5>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="6">
            <h5>Data de Recebimento: {{ complaint.createdAt | datetime }}</h5>
          </b-col>
          <b-col cols="6">
            <h5>Última Movimentação: {{ complaint.updatedAt | datetime }}</h5>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="6">
            <h5>
              Primeira Visualização:
              <template v-if="complaint.firstView">
                {{ complaint.firstView.viewedAt | datetime }}
                <span v-if="complaint.firstView.user">
                  - {{ complaint.firstView.user.name }}
                  {{ complaint.firstView.user.surname }}
                </span>
              </template>
              <template v-else>
                <span class="text-muted">Sem visualizações</span>
              </template>
            </h5>
          </b-col>
          <b-col cols="6">
            <h5>
              Última Visualização:
              <template v-if="complaint.lastView">
                {{ complaint.lastView.viewedAt | datetime }}
                <span v-if="complaint.lastView.user">
                  - {{ complaint.lastView.user.name }}
                  {{ complaint.lastView.user.surname }}
                </span>
              </template>
              <template v-else>
                <span class="text-muted">Sem visualizações</span>
              </template>
            </h5>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="6">
            <h5>
              Número sequencial:
              <strong>{{ complaint.companyId }}</strong>
            </h5>
          </b-col>
          <b-col cols="6">
            <h5>Portal de Recebimento: /{{ complaint.portal.slug }}</h5>
          </b-col>
        </b-row>
        <b-row v-if="hasNatureOrLevel">
          <b-col cols="6">
            <h5
              v-if="complaint.classification && complaint.classification.nature"
            >
              Natureza: <strong>{{ getNatureText }}</strong>
            </h5>
          </b-col>
          <b-col cols="6">
            <h5
              v-if="complaint.classification && complaint.classification.level"
            >
              Risco: <strong>{{ getLevelText }}</strong>
            </h5>
          </b-col>
        </b-row>
      </fieldset>
      <fieldset class="pb-3">
        <legend>
          {{
            complaint.anonymous ? "Denúncia Anônima" : "Denúncia Identificada"
          }}
        </legend>
      </fieldset>
      <b-row v-if="complaint.typology" class="pb-3">
        <b-col md="12">
          <p>
            <strong>Tipologia:</strong>
            {{ typologyValues[complaint.typology] }}
          </p>
          <p v-if="complaint.denouncedRelation">
            <strong>Cargo da pessoa relatada/denunciada:</strong>
            {{ denouncedRelationValues[complaint.denouncedRelation] }}
          </p>
        </b-col>
      </b-row>
      <b-row class="pb-3">
        <b-col md="12">
          <h5>Descrição</h5>
          <mention-text
            :mention="currentMention"
            :text="complaint.text"
            entry-key="text"
          />
          <template v-if="complaint.submittedClassification">
            <hr class="hr-text" data-content="Indicadores informados" />
            <template v-for="opt in Object.values(ClassificationTypeMap)">
              <div
                v-if="
                  complaint.submittedClassification[opt] &&
                  ClassificationName[opt]
                "
                :key="opt"
              >
                <b>{{ ClassificationName[opt] }}:</b>
                {{
                  (classificationMaps[opt] || {})[
                    complaint.submittedClassification[opt]
                  ]
                }}<br />
              </div>
            </template>
            <hr class="hr-text" />
          </template>
        </b-col>
      </b-row>

      <template v-if="hasAlternateComplaintForm">
        <b-row class="pb-3">
          <b-col cols="4">
            <label><strong>Contrato / Projeto / Setor:</strong></label>
            {{ complaint.relatedProject | noInformation }}
          </b-col>
          <b-col cols="4">
            <label><strong>Posto:</strong></label>
            {{ complaint.relatedOffice | noInformation }}
          </b-col>
          <b-col cols="4">
            <label><strong>Cidade:</strong></label>
            {{ complaint.location | noInformation }}
          </b-col>
        </b-row>
        <b-row v-if="complaint.files.length > 0">
          <b-col md="12">
            <h5>Anexos</h5>
            <b-list-group>
              <b-list-group-item
                v-for="(file, idx) in complaint.files"
                :key="idx"
                class="d-flex justify-content-between align-items-center"
              >
                {{ file.name }}
                <b-button @click="$emit('download-file', file)">
                  <i class="fa fa-download" aria-hidden="true"></i>
                </b-button>
              </b-list-group-item>
            </b-list-group>
          </b-col>
        </b-row>
      </template>
      <template v-else>
        <b-button
          variant="link"
          class="float-right"
          @click="fullComplaintView = !fullComplaintView"
        >
          <template v-if="fullComplaintView"
            >Comprimir <i class="fas fa-minus" aria-hidden="true"></i
          ></template>
          <template v-else
            >Expandir <i class="fas fa-plus" aria-hidden="true"></i
          ></template>
        </b-button>
        <b-collapse id="complaint-info" v-model="fullComplaintView">
          <hr class="expandable-separator" />
          <b-row v-if="complaint.submittedClassification" class="pb-3">
            <b-col md="12">
              <h5>Indicadores informados</h5>
              <template v-for="opt in Object.values(ClassificationTypeMap)">
                <div v-if="complaint.submittedClassification[opt]" :key="opt">
                  <b>{{ ClassificationName[opt] }}:</b>
                  {{
                    classificationMaps[opt][
                      complaint.submittedClassification[opt]
                    ]
                  }}<br />
                </div>
              </template>
            </b-col>
          </b-row>
          <b-row class="pb-3">
            <b-col md="12">
              <h5>Data dos fatos</h5>
              <mention-text
                :mention="currentMention"
                :text="complaint.date | noInformation"
                entry-key="date"
              />
            </b-col>
          </b-row>
          <b-row class="pb-3">
            <b-col md="12">
              <h5>Local onde ocorreram os fatos</h5>
              <mention-text
                :mention="currentMention"
                :text="complaint.location | noInformation"
                entry-key="location"
              />
            </b-col>
          </b-row>
          <b-row class="pb-3">
            <b-col md="12">
              <h5>Como teve conhecimento dos fatos</h5>
              <mention-text
                :mention="currentMention"
                :text="complaint.knowAbout | noInformation"
                entry-key="knowAbout"
              />
            </b-col>
          </b-row>
          <b-row class="pb-3">
            <b-col md="12">
              <h5>Testemunhas ou pessoas que tiverem conhecimento</h5>
              <mention-text
                :mention="currentMention"
                :text="complaint.witnesses | noInformation"
                entry-key="witnesses"
              />
            </b-col>
          </b-row>
          <b-row class="pb-3">
            <b-col md="12">
              <h5>Danos financeiros</h5>
              <mention-text
                :mention="currentMention"
                :text="complaint.financialInfo | noInformation"
                entry-key="financialInfo"
              />
            </b-col>
          </b-row>
          <b-row
            v-if="
              !complaint.anonymous &&
              (!complaint.committee ||
                !complaint.committee.hideDenouncerData ||
                masterUser ||
                isComplaintAdmin)
            "
            class="pb-3"
          >
            <b-col md="12">
              <h5>Dados do Denunciante</h5>
              <b-row>
                <b-col cols="4">
                  <label>Nome:</label>
                  {{ complaint.denouncer.fullName | noInformation }}
                </b-col>
                <b-col cols="4">
                  <label>Cargo / Função:</label>
                  {{ complaint.denouncer.position | noInformation }}
                </b-col>
              </b-row>
              <b-row>
                <b-col cols="4">
                  <label>Telefone:</label>
                  {{ complaint.denouncer.phone | noInformation }}
                </b-col>
                <b-col cols="4">
                  <label>Email:</label>
                  {{ complaint.denouncer.email | noInformation }}
                </b-col>
              </b-row>
            </b-col>
          </b-row>
          <b-row v-if="complaint.files.length > 0">
            <b-col md="12">
              <h5>Anexos</h5>
              <b-list-group>
                <b-list-group-item
                  v-for="(file, idx) in complaint.files"
                  :key="idx"
                  class="d-flex justify-content-between align-items-center"
                >
                  {{ file.name }}
                  <b-button @click="$emit('download-file', file)">
                    <i class="fa fa-download" aria-hidden="true"></i>
                  </b-button>
                </b-list-group-item>
              </b-list-group>
            </b-col>
          </b-row>
        </b-collapse>
      </template>
    </b-card-body>
  </b-card>
</template>

<script>
import { getBadgeColor, getStatusText } from "../utils";
import {
  ClassificationTypeMap,
  ClassificationName,
} from "../../../../../backend/models/ClassificationType.ts";
import MentionText from "@/components/MentionText";
import typologyValues from "../../../../../staticData/typology.json";
import denouncedRelationValues from "../../../../../staticData/denouncedRelation.json";

export default {
  name: "InfoCard",
  components: { MentionText },
  props: {
    complaint: { type: Object, required: true },
    previousProtocolId: { type: [String, Number], default: null },
    previousProtocolType: { type: String, default: "complaint" },
    currentMention: { type: Object, default: null },
    classificationMaps: { type: Object, required: true },
    masterUser: { type: Boolean, default: false },
    isComplaintAdmin: { type: Boolean, default: false },
  },
  data() {
    return {
      ClassificationTypeMap,
      ClassificationName,
      fullComplaintView: false,
      getBadgeColor,
      getStatusText,
      typologyValues,
      denouncedRelationValues,
    };
  },
  computed: {
    hasAlternateComplaintForm() {
      return this.complaint.company?.modules?.complaint?.alternateForm;
    },
    hasNatureOrLevel() {
      return (
        (this.complaint.classification &&
          this.complaint.classification.nature) ||
        (this.complaint.classification && this.complaint.classification.level)
      );
    },
    getNatureText() {
      if (
        this.complaint.classification &&
        this.complaint.classification.nature
      ) {
        return (
          this.classificationMaps.nature[
            this.complaint.classification.nature
          ] || "Não informado"
        );
      }
      return "Não informado";
    },
    getLevelText() {
      if (
        this.complaint.classification &&
        this.complaint.classification.level
      ) {
        return (
          this.classificationMaps.level[this.complaint.classification.level] ||
          "Não informado"
        );
      }
      return "Não informado";
    },
  },
};
</script>

<style></style>
